/**
 * O aplicador das telas montadas sobre `SmartTable`.
 *
 * Irmão de `aplicadorDaFolhaDePonto`, e existe pela mesma razão: o casamento
 * genérico exige que o `name` do controle SEJA a chave do contrato, e numa
 * tabela o filtro se chama pela COLUNA (`filtro-name`), não pelo que a conversa
 * pediu (`busca`). Nenhuma das duas pontas está errada — falta a tradução, e
 * ela é da tela, que é quem sabe qual coluna responde por qual chave.
 *
 * Uma tela dá o mapa (`colunasPorChave`) e o localizador; este módulo devolve
 * `PedidoDeEscrita`. Quem escreve continua sendo a primitiva `escrever`, para
 * que campo sensível, invisível e ocupado sejam recusados sem a tela precisar
 * lembrar da régua.
 */
import type {
  AplicadorDeRascunhoDaTela,
  MotivoDeRecusaDoAplicador,
  PedidoDeEscrita
} from './aplicadorDeRascunhoDaTela'

/** O que a tela precisa saber sobre o filtro para planejar a escrita. */
export interface FiltroLocalizado {
  /** O que está no controle agora — vazio é vazio. */
  valor_atual: string
  /** Está à vista? Abas e colunas escondidas mudam isso. */
  visivel: boolean
  /** Escreve COMO DIGITAÇÃO: `v-model`, `v-mask` e watchers precisam rodar. */
  escrever: (valor: string) => void
  /** O que a pessoa lê no cabeçalho da coluna, para a barra de resultado. */
  rotulo?: string | null
}

export interface DependenciasDoAplicadorDeTabela {
  /**
   * Chave do contrato → `field` da coluna. Chave fora do mapa não é recusa
   * desta tela: o rascunho pode trazer campos que só o preenchimento genérico
   * entende, e inventar recusa aqui poluiria a barra com motivo que não diz
   * nada à pessoa.
   */
  colunasPorChave: Readonly<Record<string, string>>
  localizarFiltro: (campoDaColuna: string) => FiltroLocalizado | null
}

export function criarAplicadorDeTabelaInteligente (
  dependencias: DependenciasDoAplicadorDeTabela
): AplicadorDeRascunhoDaTela {
  return (rascunho: Record<string, unknown>): PedidoDeEscrita[] => {
    const pedidos: PedidoDeEscrita[] = []

    for (const [chave, bruto] of Object.entries(rascunho)) {
      const coluna = dependencias.colunasPorChave[chave]
      if (typeof coluna !== 'string' || coluna.length === 0) continue

      const valor = typeof bruto === 'string' ? bruto : String(bruto ?? '')
      const filtro = dependencias.localizarFiltro(coluna)
      if (filtro === null) {
        pedidos.push(recusa(chave, valor, chave, 'campo_nao_encontrado'))
        continue
      }

      pedidos.push({
        campo: chave,
        valor,
        rotulo: filtro.rotulo || chave,
        visivel: filtro.visivel,
        ocupado: filtro.valor_atual.trim().length > 0,
        valor_anterior: filtro.valor_atual,
        aplicar: novo => filtro.escrever(novo)
      })
    }

    return pedidos
  }
}

function recusa (
  campo: string,
  valor: string,
  rotulo: string,
  motivo: MotivoDeRecusaDoAplicador
): PedidoDeEscrita {
  return {
    campo,
    valor,
    rotulo,
    visivel: false,
    ocupado: false,
    valor_anterior: '',
    recusaPrevia: motivo,
    aplicar: () => {}
  }
}
